import Flickity from "flickity";
import IntersectionObsManager from "../managers/IntersectionObserver.js";
import { onReducedMotionChange, prefersReducedMotion } from "../helpers/reducedMotion.js";

class Slider {
    constructor(elem) {
        this.elem = elem;
        this.slider = this.elem.querySelector(".slider__inner");
        this.autoPlayEnded = false;

        this.init();
    }

    // Retires autoplay for the session, which is deliberately narrower than what Flickity
    // stops its own player on. Flickity stops on any pointerDown, and pointerDown is emitted
    // at touchstart before the gesture has a direction — so on a phone, a scroll that merely
    // begins on the slider would kill the feature for good, and the slider is full-width and
    // mid-page. Flickity's own stop still halts the current in-view period; the observer
    // revives it on the next offscreen-to-onscreen cycle. Only the events below are treated
    // as the visitor taking the wheel.
    endAutoPlay = () => {
        // A drag arrives here twice — dragStart, then uiChange. Everything below is
        // idempotent, so the guard is for the reader, not the runtime.
        if (this.autoPlayEnded) return;

        this.autoPlayEnded = true;
        this.flickitySlider.stopPlayer();
        this.observer.unobserve(this.elem);
    };

    // stop/play, not the pause/unpause pair that reads as the better fit for "offscreen" and
    // is not: Flickity already spends pause/unpause on hover *and* on tab visibility, where
    // Player.visibilityChange is `this[isPageHidden ? "pause" : "unpause"]()`. A player left
    // paused for being offscreen therefore gets resumed by a tab away and back, rotating
    // where nobody can see it — two gates cannot share one boolean. Giving this one its own
    // verbs makes them compose: stop() drops that visibilitychange listener, and a mouseleave
    // arriving after we stop finds a stopped player, which unpause() leaves alone.
    handleIntersection = (entry) => {
        // Still guarded rather than relying on unobserve alone, since a callback already
        // queued for this target can outlive the unobserve call.
        if (this.autoPlayEnded) return;

        if (entry.isIntersecting) {
            this.flickitySlider.playPlayer();

            // Chrome recomputes hover on scroll, so re-entering the viewport under a
            // parked cursor delivers mouseenter before this callback — and pause() on a
            // still-stopped player is a no-op, which would rotate the quote out from
            // under a resting pointer. Pausing after play lands the state Flickity's own
            // mouseleave → unpause() expects, so the pointer leaving still resumes.
            if (this.slider.matches(":hover")) this.flickitySlider.pausePlayer();
        } else {
            this.flickitySlider.stopPlayer();
        }
    };

    // One-way, unlike hero-cycle.js and video.js: the preference turning on has to stop a
    // rotation already in flight, but turning it back off deliberately does not restart it.
    // The physics below is read once at construction, so a slider built under reduced motion
    // keeps its instant transitions for the session — resuming autoplay would then jump-cut
    // between quotes every 10s with no animation, which is worse than not rotating. Those two
    // modules restart because their alternative is a half-built hero or a dead video frame;
    // the alternative here is a finished section that still has its page dots.
    handleMotionPreference = () => {
        if (prefersReducedMotion()) this.endAutoPlay();
    };

    init() {
        const sliderOptions = {
            autoPlay: false,
            adaptiveHeight: false,
            dragThreshold: 17,
            pageDots: false,
            prevNextButtons: false,
            wrapAround: true,
        };

        // loop slides by default
        if (this.elem.dataset.loop) {
            sliderOptions.wrapAround = this.elem.dataset.loop === "true";
        }

        // if not looping, contain slide scrolling for start/end
        if (!sliderOptions.wrapAround) {
            sliderOptions.contain = true;
        }

        // align start of slider - left, center, right
        if (this.elem.dataset.align) {
            sliderOptions.cellAlign = this.elem.dataset.align;
        }

        // group slides into specified number
        if (this.elem.dataset.group) {
            sliderOptions.groupCells = this.elem.dataset.group;
        }

        // show dots
        if (this.elem.dataset.dots) {
            sliderOptions.pageDots = this.elem.dataset.dots === "true";
        }

        if (this.elem.dataset.adaptHeight) {
            sliderOptions.adaptiveHeight = this.elem.dataset.adaptHeight === "true";
        }

        // auto-advance every N ms, opt in per slider so the module default stays put and a
        // new slider has to ask to move. Number() rather than parseInt() because parseInt
        // reads data-auto-play="10s" as 10 and would tick every 10ms; anything non-numeric
        // is NaN here, fails the comparison, and leaves autoPlay false — where a truthy
        // non-number would instead reach Flickity's own 3000ms fallback.
        const autoPlayDelay = Number(this.elem.dataset.autoPlay);
        const hasAutoPlay = autoPlayDelay > 0 && !prefersReducedMotion();

        if (hasAutoPlay) {
            sliderOptions.autoPlay = autoPlayDelay;
        }

        // Flickity animates selection with a spring: attraction applies a force toward the
        // target cell, friction damps the velocity that force builds. At 1 and 1 the force
        // is the entire remaining distance and the damping is total, so the cell is simply
        // there on the next frame — the no-animation form of the same gesture rather than a
        // disabled slider. Nothing moves unprompted either: the check above has already
        // declined to hand Flickity an interval.
        //
        // The physics is read once at construction, unlike the timers elsewhere: swapping it
        // under a drag already in flight would be a worse outcome than a slider that keeps
        // its feel until the next page load. handleMotionPreference does interrupt the player
        // loop, which cannot wait for a reload — see the note there for why one direction is
        // enough.
        if (prefersReducedMotion()) {
            sliderOptions.friction = 1;
            sliderOptions.selectedAttraction = 1;
        }

        this.flickitySlider = new Flickity(this.slider, sliderOptions);

        // Flickity captures the interval as it builds the player and registers the hover-pause
        // listener off its own 'activate' event, both during construction — so autoPlay has to
        // be in the options above rather than switched on later, and the player is already
        // running by the time this line executes. Stopping it here hands the observer below
        // sole ownership of when it runs, and the first intersection callback lands orders of
        // magnitude sooner than the first tick.
        if (hasAutoPlay) {
            this.flickitySlider.stopPlayer();

            // Built before the handlers below, all of which reach for it through endAutoPlay.
            this.observer = new IntersectionObsManager({
                rootMargin: "0% 0% 0% 0%",
                threshold: 0,
            });

            this.observer.observe(this.elem, this.handleIntersection);

            // dragStart rather than pointerDown, per the note on endAutoPlay: Flickity
            // overrides unidragger's gate with an x-axis-only test against dragThreshold, so
            // dragStart is the first point at which a gesture is known to be a carousel drag
            // and not a page scroll. uiChange covers the rest — a dot click, Enter or Space on
            // a dot (they are real buttons, so that arrives as a click), and the arrow keys
            // while a dot holds focus.
            this.flickitySlider.on("dragStart", this.endAutoPlay);
            this.flickitySlider.on("uiChange", this.endAutoPlay);

            onReducedMotionChange(this.handleMotionPreference);
        }

        window.addEventListener("load", () => {
            setTimeout(() => {
                this.flickitySlider.resize();
            }, 150);
        });
    }
}

const flickitySliderElems = document.querySelectorAll(".slider");

if (flickitySliderElems.length) {
    flickitySliderElems.forEach((elem) => {
        new Slider(elem);
    });
}
